import type { ReactNode } from 'react';
import { MESSAGE_TEXT } from './agent-activity';
import { CircleHelp, FileText, GitPullRequest, Lightbulb, ListChecks, ListPlus, Radio } from 'lucide-react';
import { splitCommentParts } from '@shared/comment-short';
import { findFactoryQuestions } from '@shared/factory-questions';
import { findFactorySuggestions } from '@shared/factory-suggestions';
import { findFactoryBacklog } from '@shared/factory-backlog';
import { findFactorySubtasks } from '@shared/factory-subtasks';
import { findFactorySpec } from '@shared/factory-spec';
import { findFactoryPr } from '@shared/factory-pr';
import { findFactoryTasks } from '@shared/factory-tasks';
import { closedFencedBlocks, parseFactoryProgressJson } from '@shared/factory-progress';
import { MentionText } from '@/components/tasks/MentionText';
import { showAgentPicture, showAgentPictures } from './show-agent-picture';
import { cn } from '@/lib/utils';

/**
 * SF-T-209: one of the coding agent's messages in the task's Agent tab: its prose as Markdown, and the factory's
 * own blocks (progress, questions, suggestions, sub-tasks, spec, PR, tasks) as small read-only cards instead of
 * raw JSON. The task's Comments tab is where they're answered and decided: the run posts them there.
 */
export function AgentMessageText({ text, className }: { text: string; className?: string }) {
  const { prose, factoryBlocks } = splitCommentParts(text);
  const blocks = factoryBlocks ? splitBlocks(factoryBlocks) : [];
  return (
    <div className={cn('space-y-2', className)}>
      {prose ? <MentionText text={prose} members={[]} markdown className={MESSAGE_TEXT} renderImage={showAgentPicture} renderImages={showAgentPictures} /> : null}
      {blocks.map((block, i) => (
        <FactoryBlockCard key={i} block={block} />
      ))}
    </div>
  );
}

/** The blocks splitCommentParts joined, one by one. */
function splitBlocks(joined: string): string[] {
  const out: string[] = [];
  let current: string[] = [];
  let fence: string | null = null;
  for (const line of joined.split('\n')) {
    if (fence === null) {
      const open = /^ {0,3}(`{3,}|~{3,})/.exec(line);
      if (!open) continue;
      fence = open[1];
      current = [line];
      continue;
    }
    current.push(line);
    if (new RegExp(`^ {0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}[ \\t]*$`).test(line)) {
      out.push(current.join('\n'));
      fence = null;
    }
  }
  if (fence !== null && current.length) out.push(current.join('\n'));
  return out;
}

function Card({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="rounded-md bg-muted/50 px-2.5 py-2 text-xs">
      <div className="flex items-center gap-1.5 font-medium">
        {icon}
        <span>{title}</span>
      </div>
      {children ? <div className="mt-1 space-y-1 text-muted-foreground">{children}</div> : null}
    </div>
  );
}

const icon = 'size-3.5 shrink-0 text-muted-foreground';

/** One factory block, as a card; one the app doesn't know (or can't read) shows as code. */
export function FactoryBlockCard({ block }: { block: string }) {
  const lang = /^ {0,3}(?:`{3,}|~{3,})[ \t]*([\w-]*)/.exec(block)?.[1] ?? '';
  if (lang === 'factory-progress') {
    const report = closedFencedBlocks(block, lang).map(parseFactoryProgressJson).find(Boolean);
    if (report) {
      return (
        <Card icon={<Radio className={icon} aria-hidden />} title={report.headline}>
          {report.detail ? <p>{report.detail}</p> : null}
          {report.steer?.length ? <p>Choices offered: {report.steer.join(' · ')}</p> : null}
        </Card>
      );
    }
  }
  if (lang === 'factory-questions' || lang === 'factory-decisions') {
    const found = findFactoryQuestions(block);
    if (found) {
      const n = found.questions.questions.length;
      return (
        <Card icon={<CircleHelp className={icon} aria-hidden />} title={`${n} question${n === 1 ? '' : 's'}${found.questions.ask ? ` for ${found.questions.ask}` : ''}`}>
          <ol className="list-decimal space-y-0.5 pl-4">
            {found.questions.questions.map((q) => (
              <li key={q.id}>
                {q.header ? <span className="font-medium text-foreground">{q.header}: </span> : null}
                {q.question}
                {q.options.length ? <span> ({q.options.map((o) => o.label).join(' / ')})</span> : null}
              </li>
            ))}
          </ol>
          <p>Answer them in the Comments tab.</p>
        </Card>
      );
    }
  }
  // SF-T-308: a decisions block in the wrap phase is suggestions.
  if (lang === 'factory-suggestions' || lang === 'factory-questions' || lang === 'factory-decisions') {
    const found = findFactorySuggestions(block);
    if (found) return <ListCard icon={<Lightbulb className={icon} aria-hidden />} noun="suggestion" items={found.suggestions.suggestions.map((s) => s.title)} note="Decide them in the Comments tab." />;
  }
  if (lang === 'factory-backlog') {
    const found = findFactoryBacklog(block);
    if (found) return <ListCard icon={<Lightbulb className={icon} aria-hidden />} noun="Backlog pick" items={found.map((i) => `${i.title ?? `Suggestion ${i.number}`}: ${i.task ? `made ${i.task}` : `no task (${i.reason})`}`)} />;
  }
  if (lang === 'factory-subtasks') {
    const found = findFactorySubtasks(block);
    if (found) return <ListCard icon={<ListChecks className={icon} aria-hidden />} noun="proposed sub-task" items={found.subtasks.subtasks.map((s) => s.title)} note="Add them in the Comments tab." />;
  }
  if (lang === 'factory-tasks') {
    const found = findFactoryTasks(block);
    if (found) return <ListCard icon={<ListPlus className={icon} aria-hidden />} noun="new task" items={found.tasks.map((t) => t.title)} />;
  }
  if (lang === 'factory-spec') {
    const found = findFactorySpec(block);
    if (found) {
      return (
        <Card icon={<FileText className={icon} aria-hidden />} title="Expanded spec">
          <MentionText text={found.spec} members={[]} markdown className="line-clamp-6 text-xs text-foreground" />
        </Card>
      );
    }
  }
  if (lang === 'factory-pr') {
    const found = findFactoryPr(block);
    if (found) {
      return (
        <Card icon={<GitPullRequest className={icon} aria-hidden />} title={found.pr ? 'The work went in another pull request' : 'No pull request needed'}>
          {found.pr ? (
            <a href={found.pr} target="_blank" rel="noreferrer" className="break-all text-primary underline-offset-2 hover:underline">
              {found.pr}
            </a>
          ) : null}
          {found.reason ? <p>{found.reason}</p> : null}
        </Card>
      );
    }
  }
  return <pre className="overflow-x-auto rounded bg-muted/60 px-1.5 py-1 font-mono text-2xs">{block}</pre>;
}

function ListCard({ icon, noun, items, note }: { icon: ReactNode; noun: string; items: string[]; note?: string }) {
  return (
    <Card icon={icon} title={`${items.length} ${noun}${items.length === 1 ? '' : 's'}`}>
      <ul className="list-disc space-y-0.5 pl-4">
        {items.map((title, i) => (
          <li key={i}>{title}</li>
        ))}
      </ul>
      {note ? <p>{note}</p> : null}
    </Card>
  );
}
